import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getSpecialization } from 'api/shortlister'
import { researchSessionStore } from 'shared/stores/researchSessionStore'
import { removeEmojiPrefix } from 'shared/utils/removeEmojiPrefix'
import { reconcileCourseSelection } from '../../../utils/researchSelectionValidation'

export function useResearchCourse({
	career,
	draftCategory,
	draftSubCategory,
	draftCountry,
	draftCourse,
	activeDegree,
	levels,
	subCategories,
	loadingDetails,
	loadingCategory,
	loadingCountries,
	isValidCareer,
	isSelectedCountry,
	isSelectedCourse,
	selectedCourses,
	setIsSelectedCourse,
	setSelectedCourses,
}) {
	const [courses, setCourses] = useState([])
	const [loadingCourses, setLoadingCourses] = useState(true)
	const [coursesError, setCoursesError] = useState(null)
	const isValidCourse = useRef(false)
	const coursesRequestGeneration = useRef(0)
	const [coursesRetryToken, setCoursesRetryToken] = useState(0)

	const resetCourses = useCallback(() => {
		coursesRequestGeneration.current += 1
		setCourses([])
		setLoadingCourses(true)
		isValidCourse.current = false
		setCoursesError(null)
	}, [])

	//Get Courses
	useEffect(() => {
		if (
			loadingDetails ||
			loadingCategory ||
			loadingCountries ||
			!isValidCareer?.current
		) {
			if (career && draftSubCategory) {
				setLoadingCourses(true)
			}
			return
		}

		const category = draftCategory || ''
		const subCategoryValue = draftSubCategory || ''
		const careerValue = career || ''
		const country = draftCountry || ''
		const degreeValue = activeDegree || ''

		const fetchSpecializations = async () => {
			if (!careerValue) return

			const gen = ++coursesRequestGeneration.current
			setCourses([])
			setLoadingCourses(true)
			setCoursesError(null)

			try {
				let fullCategory = category
				let fullSubCategory = subCategoryValue

				if (levels?.length && category) {
					const foundCategory = levels.find(level => {
						const cleanedLevelTitle = removeEmojiPrefix(level)
						return cleanedLevelTitle === category
					})

					if (foundCategory) {
						fullCategory = removeEmojiPrefix(foundCategory)
					}
				}

				if (subCategories?.length && subCategoryValue) {
					const foundSubCategory = subCategories.find(sub => {
						const cleanedSubTitle = removeEmojiPrefix(sub)
						return cleanedSubTitle === subCategoryValue
					})

					if (foundSubCategory) {
						fullSubCategory = removeEmojiPrefix(foundSubCategory)
					}
				}

				const currentCountry = country || ''

				const result = await getSpecialization(
					removeEmojiPrefix(fullCategory),
					removeEmojiPrefix(fullSubCategory),
					currentCountry,
					degreeValue,
				)

				if (gen !== coursesRequestGeneration.current) return

				const formattedCourses = (result?.data[0] || []).map(courseItem => {
					const subjects =
						typeof courseItem.subjects === 'string'
							? JSON.parse(courseItem.subjects.replace(/'/g, '"'))
							: courseItem.subjects
					return {
						...courseItem,
						subjects,
					}
				})

				setCourses(formattedCourses)
				setCoursesError(null)
			} catch (error) {
				if (gen !== coursesRequestGeneration.current) return
				console.error('Error fetching specializations:', error)
				setCourses([])
				setCoursesError(error)
			} finally {
				if (gen === coursesRequestGeneration.current) {
					setLoadingCourses(false)
				}
			}
		}

		if (careerValue && subCategoryValue) {
			fetchSpecializations()
		} else {
			setCourses([])
			setLoadingCourses(false)
			setCoursesError(null)
		}
	}, [
		career,
		draftSubCategory,
		draftCategory,
		draftCountry,
		activeDegree,
		levels,
		subCategories,
		loadingDetails,
		loadingCountries,
		loadingCategory,
		coursesRetryToken,
		isValidCareer,
	])

	// Default / validate draft course after catalog load — preserve multi-select when possible.
	useEffect(() => {
		if (loadingDetails || loadingCourses || loadingCountries) return
		if (coursesError) return

		if (!courses?.length) {
			if (isSelectedCourse && selectedCourses.length) {
				setIsSelectedCourse(false)
				setSelectedCourses([])
			}
			if (draftCourse) {
				researchSessionStore.updateDraft({ course: '' })
			}
			isValidCourse.current = false
			return
		}

		if (isSelectedCourse && selectedCourses.length) {
			const reconciled = reconcileCourseSelection({
				selectedCourses,
				availableCourses: courses,
				isSelectedCourse,
				draftCourse,
			})
			if (reconciled.selectedCourses.length) {
				if (reconciled.changed) {
					setSelectedCourses(reconciled.selectedCourses)
					researchSessionStore.updateDraft({
						course: reconciled.draftCourse,
					})
				}
				setIsSelectedCourse(true)
				isValidCourse.current = true
				return
			}
			setIsSelectedCourse(false)
			setSelectedCourses([])
			researchSessionStore.updateDraft({ course: reconciled.draftCourse })
			isValidCourse.current = true
			return
		}

		if (!draftCourse) {
			const firstSpecialization = courses[0].specialization
			const seeded = researchSessionStore.seedCourseIfEmpty({
				course: firstSpecialization,
			})
			if (!seeded) {
				researchSessionStore.updateDraft({
					course: firstSpecialization,
				})
			}
			setIsSelectedCourse(false)
			isValidCourse.current = true
			return
		}

		const isCourseValid = courses.some(el => el.specialization === draftCourse)
		if (!isCourseValid) {
			researchSessionStore.updateDraft({
				course: courses[0].specialization,
			})
			setIsSelectedCourse(false)
		}
		isValidCourse.current = true
	}, [
		courses,
		draftCourse,
		loadingDetails,
		loadingCourses,
		loadingCountries,
		coursesError,
		isSelectedCourse,
		selectedCourses,
		setIsSelectedCourse,
		setSelectedCourses,
	])

	const retryCourses = useCallback(() => {
		if (loadingCourses) return
		resetCourses()
		setCoursesRetryToken(token => token + 1)
	}, [loadingCourses, resetCourses])

	const courseCards = useMemo(() => {
		let list = courses || []
		// Student CourseScreen display filter
		if (
			(draftCountry === 'New Zealand' || draftCountry === 'Germany') &&
			activeDegree === 'Bachelors' &&
			isSelectedCountry
		) {
			list = []
		}
		return list.map((item, index) => {
			const subjects = Array.isArray(item.subjects)
				? item.subjects.map(subject =>
						typeof subject === 'string'
							? subject
							: subject?.name || String(subject),
					)
				: []
			const professions = Array.isArray(item.professions)
				? item.professions
				: typeof item.professions === 'string'
					? item.professions
							.split(',')
							.map(entry => entry.trim())
							.filter(Boolean)
					: []
			return {
				id: String(item.specialization || item.course_id || index),
				title: item.specialization || '',
				professions,
				tuition:
					item.tution_fees != null && item.tution_fees !== ''
						? String(item.tution_fees)
						: item.tuition_fees != null && item.tuition_fees !== ''
							? String(item.tuition_fees)
							: '',
				duration: item.duration != null ? String(item.duration) : '',
				description: item.description || '',
				subjects,
				raw: item,
			}
		})
	}, [courses, draftCountry, activeDegree, isSelectedCountry])

	return {
		courses,
		courseCards,
		loadingCourses,
		coursesError,
		resetCourses,
		retryCourses,
		isValidCourse,
	}
}
